import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import "./dropdown.scss";
import { getDropdownStyles, getGroupHeadingStyle } from "./dropdown-styles";

const DropdownAtom = forwardRef(({
	label,
	className = "",
	placeholder,
	value = "",
	options = [],
	onChange,
	error = "",
	required = false,
	sortOptions = true,
	disabled = false,
	shouldTranslate = false,
	loading = false,
	noOptionsMessage = "filter-bar.dropdown.no-options",
	onFocus = () => { },
	onBlur = () => { },
	testId,
	isSearchable = true,
	isClearable = false,
	menuPlacement = "auto",
	isMulti = false,
	variant = "primary",
	size = "medium",
	icon = null,
	isGrouped = false,
	isCollapsible = false,
	autoFocus = false,
	closeMenuOnScroll = () => { },
	isCreatable = false,
	formatCreateLabel,
	onKeyDown,
	onInputChange,
}, ref) => {
	const { t } = useTranslation();
	const selectRef = useRef(null);
	const SelectComponent = isCreatable ? CreatableSelect : Select;

	// Expose focus method to parent components
	useImperativeHandle(ref, () => ({
		focus: () => {
			if (selectRef.current) selectRef.current.focus();
		},
		blur: () => {
			if (selectRef.current) selectRef.current.blur();
		},
	}), []);

	// Process and sort options once using useMemo for performance
	const processedOptions = useMemo(() => {
		if (!options || options.length === 0) {
			return [];
		}

		// Handle grouped options
		if (isGrouped && Array.isArray(options) && options[0]?.options) {
			return options.map(group => ({
				...group,
				label: shouldTranslate ? t(group.label) : group.label,
				options: group.options.map(option => ({
					...option,
					label: shouldTranslate ? t(option.label) : option.label
				}))
			}));
		}

		// Handle regular options (existing logic)
		const optionsCopy = [...options].map(option => ({
			...option,
			label: shouldTranslate ? t(option.label) : option.label
		}));

		if (sortOptions) {
			return optionsCopy.sort((op1, op2) => {
				if (op1.isDisabled !== op2.isDisabled) {
					return op1.isDisabled ? 1 : -1;
				}
				if (!op1.label || !op2.label) return 0;
				return op1.label.localeCompare(op2.label);
			});
		}

		return optionsCopy.sort((op1, op2) => {
			if (op1.isDisabled !== op2.isDisabled) {
				return op1.isDisabled ? 1 : -1;
			}
			return 0;
		});
	}, [options, sortOptions, shouldTranslate, t, isGrouped]);

	// Format the selected value to work with react-select
	const selectedValue = useMemo(() => {
		if (value === null || value === undefined || value === '') return null;

		// Handle grouped options
		if (isGrouped && Array.isArray(processedOptions)) {
			for (const group of processedOptions) {
				if (group.options) {
					const found = group.options.find(option => option.value === value || option.value === Number(value));
					if (found) return found;
				}
			}
			return null;
		}

		// Handle regular options
		return processedOptions.find(option => option.value === value || option.value === Number(value)) || null;
	}, [value, processedOptions, isGrouped]);

	// Handle changes in react-select
	const handleChange = (selectedOption) => {
		onChange({
			target: {
				// Ensure we pass the actual value (including 0) instead of empty string when null
				value: selectedOption ? selectedOption.value : ""
			}
		});
	};

	// Determine sizing classes based on size prop
	const getSizeClass = () => {
		switch (size) {
			case 'small':
				return 'dropdown-sm';
			case 'large':
				return 'dropdown-lg';
			default:
				return '';
		}
	};

	// Determine variant classes
	const getVariantClass = () => {
		switch (variant) {
			case 'secondary':
				return 'dropdown-secondary';
			case 'outline':
				return 'dropdown-outline';
			default:
				return 'dropdown-primary';
		}
	};

	// Custom styles for react-select - see ./dropdown-styles.js
	// (react-select requires a JS styles object, not CSS/SCSS, because each
	//  style function receives live component state: isFocused, isSelected, etc.)
	const customStyles = getDropdownStyles({ size, error, disabled, isCollapsible });

	// Custom components for collapsible groups
	const customComponents = isCollapsible ? {
		Group: ({ children, ...props }) => {
			const [isExpanded, setIsExpanded] = React.useState(true);

			return (
				<div>
					<div
						style={getGroupHeadingStyle({ size, isCollapsible })}
						onClick={() => setIsExpanded(!isExpanded)}
					>
						<span>{props.label}</span>
						<span className={`dd-expand-chevron${isExpanded ? " dd-expand-chevron--expanded" : ""}`}>
							▶
						</span>
					</div>
					{isExpanded && (
						<div>
							{children}
						</div>
					)}
				</div>
			);
		}
	} : {};

	return (
		<div className={`dropdown-container ${getSizeClass()} ${getVariantClass()} ${className}`}>
			{label && (
				<label className="mb-0">
					{icon && <span className="dropdown-icon me-1">{icon}</span>}
					{label}
					{required && <span className="text-danger"> *</span>}
				</label>
			)}

			<SelectComponent
				ref={selectRef}
				className={`react-select-container`}
				classNamePrefix="react-select"
				placeholder={loading
					? t("common.loading")
					: placeholder || t("filter-bar.dropdown.select")}
				value={selectedValue}
				onChange={handleChange}
				options={processedOptions}
				isDisabled={disabled || loading}
				isLoading={loading}
				isClearable={isClearable}
				isSearchable={isSearchable}
				onFocus={onFocus}
				onBlur={onBlur}
				styles={customStyles}
				components={customComponents}
				data-testid={testId}
				noOptionsMessage={() => t(noOptionsMessage)}
				menuPlacement={menuPlacement}
				menuPortalTarget={document.body}
				menuPosition="fixed"
				isMulti={isMulti}
				autoFocus={autoFocus}
				closeMenuOnScroll={closeMenuOnScroll}
				{...(isCreatable && {
					formatCreateLabel: formatCreateLabel ?? ((inputValue) => `${t("common.create")}: "${inputValue}"`),
				})}
				{...(onKeyDown && { onKeyDown })}
				{...(onInputChange && { onInputChange })}
			/>

			{error && <div className="invalid-feedback d-block">{error}</div>}
		</div>
	);
});

DropdownAtom.displayName = 'DropdownAtom';

export default DropdownAtom;
